/*
 * Cart drawer (sections/cart-drawer.liquid)
 *
 * The drawer is rendered by Liquid and re-rendered through the Section
 * Rendering API after every cart change, so this file only has to:
 *  - open / close the panel (and lock page scroll while open)
 *  - forward quantity / remove / subscription / upsell actions to the Cart API
 *  - watch every other cart request on the page (theme, apps) and refresh
 *  - keep the header cart count in sync
 *  - add / remove the free reward product the rewards tiers ask for
 *
 * It also exposes the window.upcart* functions the theme was already calling
 * (section-navigation-main.js opens the cart with window.upcartOpenCart()).
 */
(() => {
  if (customElements.get('cart-drawer')) return;

  const CART_REQUEST = /\/cart\/(add|change|update|clear)(\.js|\.json)?(\?|$)/;
  const INTERNAL_HEADER = 'X-Cart-Drawer';
  const rootUrl = (window.routes && window.routes.root) || '/';
  const cartRoutes = {
    add: (window.routes && window.routes.cartAdd) || '/cart/add',
    change: (window.routes && window.routes.cartChange) || '/cart/change',
    update: (window.routes && window.routes.cartUpdate) || '/cart/update',
  };

  const FOCUSABLE =
    'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

  class CartDrawer extends HTMLElement {
    constructor() {
      super();
      this.onClick = this.onClick.bind(this);
      this.onChange = this.onChange.bind(this);
      this.onKeydown = this.onKeydown.bind(this);
      this.onInputKeydown = this.onInputKeydown.bind(this);
      this.onDocumentClick = this.onDocumentClick.bind(this);
      this.onDocumentSubmit = this.onDocumentSubmit.bind(this);
      this.refreshTimer = null;
      this.giftAttempts = new Map();
      this.giftPending = false;
      this.giftSettled = false;
      this.carouselIndex = 0;
    }

    connectedCallback() {
      this.sectionId = this.dataset.sectionId;
      this.addEventListener('click', this.onClick);
      this.addEventListener('change', this.onChange);
      this.addEventListener('keydown', this.onInputKeydown);
      document.addEventListener('click', this.onDocumentClick, true);
      document.addEventListener('submit', this.onDocumentSubmit, true);
      this.registerGlobals();
      this.afterRender();
      if (window.__cartDrawerPendingOpen) {
        window.__cartDrawerPendingOpen = false;
        this.open();
      }

      if (window.Shopify && window.Shopify.designMode) {
        document.addEventListener('shopify:section:select', (event) => {
          if (event.target.contains(this)) this.open();
        });
        document.addEventListener('shopify:section:deselect', (event) => {
          if (event.target.contains(this)) this.close();
        });
      }
    }

    disconnectedCallback() {
      document.removeEventListener('click', this.onDocumentClick, true);
      document.removeEventListener('submit', this.onDocumentSubmit, true);
      document.removeEventListener('keydown', this.onKeydown);
    }

    /* ------------------------------------------------------------ state */

    get isOpen() {
      return this.classList.contains('is-open');
    }

    get panel() {
      return this.querySelector('[data-cart-drawer-panel]');
    }

    get openOnAdd() {
      return this.dataset.openOnAdd === 'true';
    }

    open() {
      if (this.isOpen) return;
      this.lastFocused = document.activeElement;
      this.lockScroll(true);
      this.classList.add('is-open');
      this.setAttribute('aria-hidden', 'false');
      document.addEventListener('keydown', this.onKeydown);
      window.requestAnimationFrame(() => this.panel && this.panel.focus({ preventScroll: true }));
      this.dispatch('cart-drawer:open');
    }

    close() {
      if (!this.isOpen) return;
      this.classList.remove('is-open');
      this.setAttribute('aria-hidden', 'true');
      this.lockScroll(false);
      document.removeEventListener('keydown', this.onKeydown);
      if (this.lastFocused && typeof this.lastFocused.focus === 'function') {
        this.lastFocused.focus({ preventScroll: true });
      }
      this.dispatch('cart-drawer:close');
    }

    toggle() {
      this.isOpen ? this.close() : this.open();
    }

    lockScroll(lock) {
      const html = document.documentElement;
      const nav = document.getElementById('nav-wrapper');
      if (lock) {
        // compensate for the scrollbar so the page does not shift sideways
        const scrollbar = window.innerWidth - html.clientWidth;
        html.classList.add('cart-drawer-open');
        document.body.style.paddingRight = scrollbar > 0 ? `${scrollbar}px` : '';
        if (nav) nav.style.paddingRight = scrollbar > 0 ? `${scrollbar}px` : '';
      } else {
        html.classList.remove('cart-drawer-open');
        document.body.style.paddingRight = '';
        if (nav) nav.style.paddingRight = '';
      }
    }

    dispatch(name, detail = {}) {
      this.dispatchEvent(new CustomEvent(name, { bubbles: true, detail }));
      document.dispatchEvent(new CustomEvent(name, { detail }));
    }

    /* ----------------------------------------------------------- events */

    onDocumentClick(event) {
      const trigger = event.target.closest(
        '#cart-open, button[name="cart-open"], [data-cart-drawer-open], a.header__icons--cart-open, a[href$="/cart"]'
      );
      if (!trigger || this.contains(trigger)) return;
      // plain /cart links only open the drawer when the theme's cart UX is the mini cart
      const uxCart = window.cartStrings && window.cartStrings.uxCart;
      if (trigger.tagName === 'A' && uxCart && uxCart !== 'mini-cart') return;
      event.preventDefault();
      this.open();
    }

    /*
     * Add-to-cart forms. The drawer submits them itself (as UpCart did) instead
     * of leaving it to base.js, whose handler expects a .cart-icon/.loading-icon
     * inside the button and throws on the theme's product form without them.
     * Capturing on document lets us stop the event before it reaches base.js.
     */
    onDocumentSubmit(event) {
      const form = event.target;
      if (!(form instanceof HTMLFormElement)) return;
      const action = form.getAttribute('action') || '';
      if (!/\/cart\/add/.test(action) || form.hasAttribute('data-cart-drawer-ignore')) return;
      const uxCart = window.cartStrings && window.cartStrings.uxCart;
      if (uxCart && uxCart !== 'mini-cart') return;
      event.preventDefault();
      event.stopPropagation();
      this.submitForm(form);
    }

    async submitForm(form) {
      const button = form.querySelector('button[type="submit"], [name="add"]');
      const errorBox = form.querySelector('.main-product__error, [data-cart-drawer-error]');
      const body = new FormData(form);
      body.append('sections', this.sectionId);
      body.append('sections_url', rootUrl);

      if (button) {
        button.setAttribute('aria-busy', 'true');
        button.classList.add('is-adding');
      }
      if (errorBox) errorBox.innerHTML = '';
      document.body.classList.remove('added-to-cart');
      this.setBusy(true);
      // open straight away (UpCart did the same, with skeletons); the busy state
      // dims the stale content until the re-render lands
      if (this.openOnAdd) this.open();

      try {
        const response = await fetch(cartRoutes.add, {
          method: 'POST',
          headers: { Accept: 'application/json', [INTERNAL_HEADER]: '1' },
          body,
        });
        const data = await response.json();
        if (data.status) {
          const message =
            typeof data.description === 'object' && data.description !== null
              ? Object.values(data.description).join(' ')
              : data.description || data.message || (window.cartStrings && window.cartStrings.error) || '';
          if (errorBox) {
            const icon = document.createElement('span');
            icon.className = 'icon loaded';
            icon.textContent = 'error';
            const text = document.createElement('p');
            text.textContent = message;
            errorBox.append(icon, text);
          }
          this.dispatch('cart-drawer:add-error', { form, message });
          return;
        }
        this.renderFrom(data);
        document.body.classList.add('added-to-cart');
        this.dispatch('cart-drawer:added', { form, item: data });
      } catch (error) {
        console.error('[cart-drawer] add to cart failed', error);
        await this.refresh({ open: this.openOnAdd });
      } finally {
        if (button) {
          button.removeAttribute('aria-busy');
          button.classList.remove('is-adding');
        }
        this.setBusy(false);
      }
    }

    onKeydown(event) {
      if (event.key === 'Escape') {
        event.preventDefault();
        this.close();
        return;
      }
      if (event.key !== 'Tab' || !this.panel) return;
      const focusable = Array.from(this.panel.querySelectorAll(FOCUSABLE)).filter((el) => el.offsetParent !== null);
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && (document.activeElement === first || document.activeElement === this.panel)) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    onClick(event) {
      const target = event.target;

      if (target.closest('[data-cart-drawer-close]')) {
        event.preventDefault();
        this.close();
        return;
      }

      const minus = target.closest('[data-qty-minus]');
      const plus = target.closest('[data-qty-plus]');
      if (minus || plus) {
        const item = target.closest('[data-line]');
        const input = item.querySelector('[data-qty-input]');
        const current = parseInt(input.value, 10) || 0;
        this.changeLine(item, { quantity: minus ? current - 1 : current + 1 });
        return;
      }

      const remove = target.closest('[data-remove-line]');
      if (remove) {
        const item = target.closest('[data-line]');
        if (item && item.dataset.giftKey) this.declineGift(item.dataset.giftKey);
        this.changeLine(item, { quantity: 0 });
        return;
      }

      // subscription card: "Ja, stap over", the active switch and "Terug naar eenmalige aankoop"
      const subscription = target.closest('[data-subscription-changes]');
      if (subscription) {
        let changes = [];
        try {
          changes = JSON.parse(subscription.dataset.subscriptionChanges || '[]');
        } catch (error) {
          changes = [];
        }
        subscription.disabled = true;
        if (subscription.getAttribute('role') === 'switch') subscription.setAttribute('aria-checked', 'false');
        this.changeLines(changes.map((change) => ({ id: change.key, quantity: change.quantity, selling_plan: change.plan })));
        return;
      }

      if (target.closest('[data-subscription-dismiss]')) {
        const card = target.closest('[data-subscription-card]');
        const keys = card ? (card.dataset.offerKeys || '').split(',').filter(Boolean) : [];
        this.dismissed = { ...this.dismissed, offer: [...new Set([...(this.dismissed.offer || []), ...keys])] };
        this.applyDismissals();
        return;
      }

      if (target.closest('[data-upsell-dismiss]')) {
        this.dismissed = { ...this.dismissed, upsells: true };
        this.applyDismissals();
        return;
      }

      const upsellAdd = target.closest('[data-upsell-add]');
      if (upsellAdd) {
        const tile = upsellAdd.closest('.cart-drawer__upsell-tile');
        const select = tile && tile.querySelector('[data-upsell-variant]');
        const variantId = select ? select.value : upsellAdd.dataset.upsellAdd;
        upsellAdd.disabled = true;
        this.addToCart({ id: Number(variantId), quantity: 1 }).finally(() => {
          upsellAdd.disabled = false;
        });
        return;
      }

      if (target.closest('[data-carousel-prev]')) {
        this.moveCarousel(-1);
        return;
      }
      if (target.closest('[data-carousel-next]')) {
        this.moveCarousel(1);
      }
    }

    onChange(event) {
      const target = event.target;

      if (target.matches('[data-qty-input]')) {
        const item = target.closest('[data-line]');
        const value = parseInt(target.value.replace(/\D/g, ''), 10);
        if (Number.isNaN(value)) {
          target.value = target.defaultValue;
          return;
        }
        if (value === Number(target.defaultValue)) return;
        this.changeLine(item, { quantity: value });
        return;
      }

      if (target.matches('[data-selling-plan-select]')) {
        const item = target.closest('[data-line]');
        target.disabled = true;
        this.changeLine(item, { selling_plan: target.value ? target.value : null });
        return;
      }

      if (target.matches('[data-upsell-variant]')) {
        const option = target.selectedOptions[0];
        const tile = target.closest('.cart-drawer__upsell-tile');
        const price = tile.querySelector('[data-upsell-price]');
        const compare = tile.querySelector('[data-upsell-compare]');
        if (price && option.dataset.price) price.textContent = option.dataset.price;
        if (compare) {
          compare.textContent = option.dataset.compare || '';
          compare.hidden = !option.dataset.compare;
        }
        const button = tile.querySelector('[data-upsell-add]');
        if (button) button.dataset.upsellAdd = target.value;
      }
    }

    onInputKeydown(event) {
      if (!event.target.matches('[data-qty-input]')) return;
      if (event.key === 'Enter') {
        event.preventDefault();
        event.target.dispatchEvent(new Event('change', { bubbles: true }));
      } else if (event.key === 'ArrowUp' || event.key === 'ArrowDown') {
        event.preventDefault();
        const item = event.target.closest('[data-line]');
        const current = parseInt(event.target.value, 10) || 0;
        this.changeLine(item, { quantity: event.key === 'ArrowUp' ? current + 1 : current - 1 });
      }
    }

    /* ------------------------------------------------------- cart calls */

    // `sections` makes Shopify render the whole drawer server-side and is by
    // far the expensive part of a cart call, so callers that are about to make
    // another one pass withSections: false and render only from the last.
    request(url, body, { withSections = true } = {}) {
      const payload = withSections ? { ...body, sections: this.sectionId, sections_url: rootUrl } : { ...body };
      return fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          [INTERNAL_HEADER]: '1',
        },
        body: JSON.stringify(payload),
      });
    }

    async changeLine(item, changes) {
      if (!item) return;
      const line = Number(item.dataset.line);
      const input = item.querySelector('[data-qty-input]');
      const quantity = 'quantity' in changes ? Math.max(0, changes.quantity) : parseInt(input ? input.value : '1', 10) || 1;
      const payload = { line, quantity };
      if ('selling_plan' in changes) payload.selling_plan = changes.selling_plan;

      this.setBusy(true);
      try {
        const response = await this.request(cartRoutes.change, payload);
        const data = await response.json();
        if (data.status || data.errors) {
          this.showLineError(item, data.description || data.message || data.errors || window.cartStrings?.error);
          await this.refresh();
          return;
        }
        this.renderFrom(data);
      } catch (error) {
        console.error('[cart-drawer] change failed', error);
        await this.refresh();
      } finally {
        this.setBusy(false);
      }
    }

    // Several /cart/change calls in a row, addressed by line key (keys of the
    // other lines don't change, indexes might). Only the last renders.
    async changeLines(changes) {
      if (!changes.length) return;
      this.setBusy(true);
      try {
        let data = null;
        for (let i = 0; i < changes.length; i += 1) {
          const response = await this.request(cartRoutes.change, changes[i], { withSections: i === changes.length - 1 });
          data = await response.json();
          if (data.status || data.errors) {
            console.warn('[cart-drawer] line change failed', data.description || data.message || data.errors);
            await this.refresh();
            return;
          }
        }
        this.renderFrom(data);
      } catch (error) {
        console.error('[cart-drawer] change failed', error);
        await this.refresh();
      } finally {
        this.setBusy(false);
      }
    }

    async addToCart(item, { open = false } = {}) {
      this.setBusy(true);
      try {
        const response = await this.request(cartRoutes.add, { items: [item] });
        const data = await response.json();
        if (data.status || data.errors) {
          this.showLineError(null, data.description || data.message || window.cartStrings?.error);
          await this.refresh();
          return false;
        }
        this.renderFrom(data);
        if (open) this.open();
        return true;
      } catch (error) {
        console.error('[cart-drawer] add failed', error);
        await this.refresh();
        return false;
      } finally {
        this.setBusy(false);
      }
    }

    showLineError(item, message) {
      const target = (item && item.querySelector('[data-line-error]')) || null;
      const text = typeof message === 'string' ? message : Array.isArray(message) ? message.join(' ') : '';
      if (target) {
        target.textContent = text;
      } else if (text) {
        console.warn('[cart-drawer]', text);
      }
    }

    setBusy(busy) {
      this.setAttribute('aria-busy', busy ? 'true' : 'false');
    }

    /* ---------------------------------------------------------- render */

    renderFrom(data) {
      const html = data && data.sections && data.sections[this.sectionId];
      if (html) {
        this.render(html);
        return true;
      }
      // response did not carry our section (older API path) — fetch it
      this.refresh();
      return false;
    }

    async refresh({ open = false } = {}) {
      const url = `${rootUrl}${rootUrl.includes('?') ? '&' : '?'}section_id=${encodeURIComponent(this.sectionId)}`;
      try {
        const response = await fetch(url, { headers: { [INTERNAL_HEADER]: '1' } });
        const html = await response.text();
        this.render(html);
      } catch (error) {
        console.error('[cart-drawer] refresh failed', error);
      } finally {
        this.setBusy(false);
      }
      if (open) this.open();
    }

    scheduleRefresh({ open = false } = {}) {
      clearTimeout(this.refreshTimer);
      this.refreshTimer = setTimeout(() => this.refresh({ open }), 60);
    }

    render(html) {
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const fresh = doc.querySelector('cart-drawer');
      if (!fresh) return;

      const body = this.querySelector('[data-cart-drawer-body]');
      const scrollTop = body ? body.scrollTop : 0;
      const freshPanel = fresh.querySelector('[data-cart-drawer-panel]');
      const panel = this.panel;
      if (panel && freshPanel) {
        // Shopify's wallet buttons (Shop Pay / Apple Pay / Google Pay) boot a
        // whole checkout bundle when they are inserted, so keep the node that
        // is already on the page instead of re-creating it on every render.
        const express = panel.querySelector('[data-cart-drawer-express]');
        const freshExpress = freshPanel.querySelector('[data-cart-drawer-express]');
        panel.setAttribute('aria-label', freshPanel.getAttribute('aria-label') || '');
        if (express && freshExpress) {
          freshExpress.replaceWith(express);
          panel.replaceChildren(...Array.from(freshPanel.childNodes));
        } else {
          panel.innerHTML = freshPanel.innerHTML;
        }
      }
      ['data-item-count', 'data-cart-value', 'data-gift-rules', 'data-open-on-add', 'data-position', 'data-title-alignment'].forEach((name) => {
        const value = fresh.getAttribute(name);
        if (value !== null) this.setAttribute(name, value);
      });
      const newBody = this.querySelector('[data-cart-drawer-body]');
      if (newBody) newBody.scrollTop = scrollTop;

      this.afterRender();
      this.dispatch('cart-drawer:updated', { itemCount: Number(this.dataset.itemCount) });
    }

    afterRender() {
      this.updateHeaderCount();
      this.applyDismissals();
      this.initCarousel();
      this.reconcileGifts();
    }

    /* ------------------------------------------------------ dismissals */

    /*
     * What the shopper closed this visit: the upsells, and the subscription
     * offer for the line keys it was showing. A line added later has a new
     * key, so the offer comes back for it.
     */
    get dismissed() {
      try {
        return JSON.parse(sessionStorage.getItem('cart-drawer:dismissed') || '{}') || {};
      } catch (error) {
        return {};
      }
    }

    set dismissed(value) {
      try {
        sessionStorage.setItem('cart-drawer:dismissed', JSON.stringify(value));
      } catch (error) {
        /* storage unavailable — dismissals last until the next render */
      }
      this.dismissedFallback = value;
    }

    applyDismissals() {
      const dismissed = { ...this.dismissedFallback, ...this.dismissed };
      const upsell = this.querySelector('[data-upsell-module]');
      if (upsell) upsell.hidden = !!dismissed.upsells;
      const offer = this.querySelector('[data-subscription-card="offer"]');
      if (offer) {
        const keys = (offer.dataset.offerKeys || '').split(',').filter(Boolean);
        const closed = dismissed.offer || [];
        offer.hidden = keys.length > 0 && keys.every((key) => closed.includes(key));
      }
    }

    updateHeaderCount() {
      const count = Number(this.dataset.itemCount) || 0;
      document.querySelectorAll('.cart-count, [data-cart-count]').forEach((el) => {
        el.textContent = count;
        el.classList.toggle('visually-hidden', count === 0);
      });
    }

    /* -------------------------------------------------------- carousel */

    initCarousel() {
      const track = this.querySelector('[data-carousel-track]');
      if (!track) return;
      const slides = track.children.length;
      this.carouselIndex = Math.min(this.carouselIndex, Math.max(0, slides - 1));
      this.applyCarousel();
    }

    moveCarousel(delta) {
      const track = this.querySelector('[data-carousel-track]');
      if (!track) return;
      const slides = track.children.length;
      this.carouselIndex = Math.min(Math.max(this.carouselIndex + delta, 0), slides - 1);
      this.applyCarousel();
    }

    applyCarousel() {
      const track = this.querySelector('[data-carousel-track]');
      if (!track) return;
      const slides = track.children.length;
      track.style.transform = `translateX(-${this.carouselIndex * 100}%)`;
      const prev = this.querySelector('[data-carousel-prev]');
      const next = this.querySelector('[data-carousel-next]');
      if (prev) prev.disabled = this.carouselIndex <= 0;
      if (next) next.disabled = this.carouselIndex >= slides - 1;
    }

    /* ------------------------------------------------------ free gifts */

    /*
     * Free-gift rules (reward tiers with a product, and "Free gift rule"
     * blocks). Each rule is {key, condition, gift}. Decisions are made against
     * a fresh /cart.js (never the rendered HTML, which can lag behind), gift
     * lines carry the `_free_gift` property, and every change gets a bounded
     * number of attempts per page load so nothing can loop. A gift the
     * customer removed by hand is not re-added until its trigger leaves the
     * cart and comes back.
     */
    get giftRules() {
      try {
        return JSON.parse((this.dataset.giftRules || '[]').replace(/,\s*]$/, ']'));
      } catch (error) {
        return [];
      }
    }

    /*
     * Gifts the shopper removed, as rule key -> how many eligible units the
     * rule matched at that moment. A removed gift stays away while the cart
     * is the same, but a new eligible product (more matching units) makes the
     * offer fresh again and it comes back. Older sessions stored a plain
     * array of keys; those read as 0 so the next eligible add re-offers.
     */
    get declinedGifts() {
      try {
        const raw = JSON.parse(sessionStorage.getItem('cart-drawer:declined-gifts') || '{}');
        if (Array.isArray(raw)) return new Map(raw.map((key) => [String(key), 0]));
        return new Map(Object.entries(raw).map(([key, units]) => [key, Number(units) || 0]));
      } catch (error) {
        return new Map();
      }
    }

    set declinedGifts(entries) {
      try {
        sessionStorage.setItem('cart-drawer:declined-gifts', JSON.stringify(Object.fromEntries(entries)));
      } catch (error) {
        /* storage unavailable — gifts simply come back */
      }
    }

    declineGift(key) {
      const declined = this.declinedGifts;
      const rule = this.giftRules.find((entry) => String(entry.key) === String(key));
      const cart = this.cartFromDom();
      const units = rule && cart ? this.ruleMatchCount(rule, cart, cart.cartValue) : 0;
      declined.set(String(key), units);
      this.declinedGifts = declined;
    }

    /*
     * The cart as the current render describes it. reconcileGifts runs right
     * after a render whose HTML the server built from the cart it had just
     * committed, so this is the same data /cart.js would return — without the
     * round trip. Returns null if the markup predates these attributes, in
     * which case the caller falls back to fetching.
     */
    cartFromDom() {
      const value = this.getAttribute('data-cart-value');
      if (value === null) return null;
      const items = [];
      for (const li of this.querySelectorAll('.cart-drawer__item[data-variant-id]')) {
        const planId = li.dataset.sellingPlan;
        items.push({
          key: li.dataset.key,
          product_id: li.dataset.productId,
          variant_id: li.dataset.variantId,
          quantity: Number(li.dataset.quantity) || 1,
          final_line_price: Number(li.dataset.linePrice) || 0,
          selling_plan_allocation: planId ? { selling_plan: { id: planId, name: li.dataset.sellingPlanName || '' } } : null,
          properties: li.dataset.giftKey ? { _free_gift: li.dataset.giftKey } : {},
        });
      }
      // Integrity check against a partial render: item_count is the summed
      // quantity, not the number of lines.
      const units = items.reduce((sum, item) => sum + item.quantity, 0);
      if (units !== Number(this.dataset.itemCount || 0)) return null;
      return { items, cartValue: Number(value) || 0 };
    }

    // Number of cart units that satisfy the rule (0 = rule not met). For
    // "lines" rules that is the summed quantity of the matching line items,
    // which drives the "one gift per item" quantity mode.
    ruleMatchCount(rule, cart, value) {
      const condition = rule.condition || {};
      if (condition.type === 'cart_value') return value >= Number(condition.threshold) ? 1 : 0;
      if (condition.type !== 'lines') return 0;
      const products = (condition.products || []).filter(Boolean).map(String);
      const variants = (condition.variants || []).filter(Boolean).map(String);
      const plans = (condition.plans || []).filter(Boolean).map((entry) => String(entry).toLowerCase().replace(/\s+/g, ''));
      return cart.items.reduce((count, item) => {
        if (item.properties && item.properties._free_gift != null) return count;
        if (products.length && !products.includes(String(item.product_id))) return count;
        if (variants.length && !variants.includes(String(item.variant_id))) return count;
        const allocation = item.selling_plan_allocation;
        if (condition.subscription === 'one_time' && allocation) return count;
        if (condition.subscription === 'required') {
          if (!allocation || !allocation.selling_plan) return count;
          if (plans.length) {
            const id = String(allocation.selling_plan.id);
            const name = String(allocation.selling_plan.name || '').toLowerCase().replace(/\s+/g, '');
            if (!plans.includes(id) && !plans.includes(name)) return count;
          }
        }
        return count + (Number(item.quantity) || 1);
      }, 0);
    }

    async reconcileGifts() {
      // A pass requested while one is running must not be dropped: the cart it
      // would have seen is newer than the one in flight. Remember it and run
      // once more at the end instead.
      if (this.giftSettled) return;
      if (this.giftBusy) {
        this.giftPending = true;
        return;
      }
      const rules = this.giftRules;
      // No rules (B2B, or none configured) still needs a pass when a gift line
      // is left over — e.g. added before a B2B buyer logged in — to remove it.
      if (!rules.length && !this.querySelector('.cart-drawer__item[data-gift-key]')) return;
      // an empty cart has nothing to add or remove — skip the extra request
      if (Number(this.dataset.itemCount) === 0) return;

      this.giftBusy = true;
      let lastData = null;
      try {
        // Prefer the cart the current render already describes; only pay for a
        // /cart.js round trip when that markup isn't available.
        const domCart = this.cartFromDom();
        let plan = domCart ? this.planGifts(rules, domCart, domCart.cartValue) : await this.planGiftsFromServer(rules);
        // Adding isn't idempotent, and the render can be stale: a section
        // refresh that left before our last gift add can land after it and
        // show the cart without the gift. Confirm against the real cart
        // before adding, or the gift goes in twice.
        if (domCart && plan.adds.length) {
          plan = await this.planGiftsFromServer(rules);
          // the gift was there all along: redraw the stale drawer (once, so a
          // refresh that is itself stale can't loop)
          if (!plan.adds.length && !Object.keys(plan.updates).length && !this.giftRedrawn) {
            this.giftRedrawn = true;
            this.scheduleRefresh();
          }
        } else {
          this.giftRedrawn = false;
        }
        const { adds, updates, touchedKeys } = plan;

        const calls = [];
        if (Object.keys(updates).length) calls.push({ route: cartRoutes.update, body: { updates } });
        if (adds.length) calls.push({ route: cartRoutes.add, body: { items: adds } });

        for (let i = 0; i < calls.length; i += 1) {
          const isLast = i === calls.length - 1;
          const response = await this.request(calls[i].route, calls[i].body, { withSections: isLast });
          const data = await response.json();
          if (data.status || data.errors) {
            touchedKeys.forEach((key) => this.giftAttempts.set(`gift:${key}`, (this.giftAttempts.get(`gift:${key}`) || 0) + 1));
            console.warn('[cart-drawer] gift change failed', data.description || data.message || data.errors);
            continue;
          }
          touchedKeys.forEach((key) => this.giftAttempts.delete(`gift:${key}`));
          if (isLast) lastData = data;
        }
      } catch (error) {
        console.error('[cart-drawer] gift update failed', error);
      } finally {
        this.giftBusy = false;
      }
      if (lastData) {
        // Our own render describes the cart we just reconciled, so the pass it
        // schedules would only re-confirm it — at the cost of another round
        // trip. Anything that changed meanwhile is caught by giftPending.
        this.giftSettled = true;
        this.renderFrom(lastData);
        this.giftSettled = false;
      }
      if (this.giftPending) {
        this.giftPending = false;
        await this.reconcileGifts();
      }
    }

    async planGiftsFromServer(rules) {
      const cart = await (await fetch(`${rootUrl}cart.js`, { headers: { [INTERNAL_HEADER]: '1' } })).json();
      const total = cart.items.reduce((sum, item) => sum + (item.properties && item.properties._free_gift != null ? item.final_line_price || 0 : 0), 0);
      return this.planGifts(rules, cart, cart.total_price - total);
    }

    // The gift adds and line updates `cart` needs to satisfy `rules`.
    planGifts(rules, cart, value) {
      const giftLines = cart.items.filter((item) => item.properties && item.properties._free_gift != null);
      const declined = this.declinedGifts;
      // Every rule's changes are gathered first so they can go out as one
      // add and one update rather than a round trip per rule.
      const adds = [];
      const updates = {};
      const touchedKeys = new Set();

      for (const rule of rules) {
        const key = String(rule.key);
        const perGift = Math.max(1, Number(rule.gift.quantity) || 1);
        const units = this.ruleMatchCount(rule, cart, value);
        const matches = units > 0;
        const wanted = rule.gift.mode === 'per_item' ? units * perGift : perGift;
        const lines = giftLines.filter((item) => String(item.properties._free_gift) === key);

        // The cap stops a change that always fails (a gift that sold out,
        // say) retrying forever. It counts failures only and is cleared on
        // success — counting successes too would mean a shopper who toggles
        // a rule on and off three times silently stops getting the gift for
        // the rest of the session.
        const blocked = (this.giftAttempts.get(`gift:${key}`) || 0) >= 3;

        if (matches && lines.length === 0) {
          const declinedAt = declined.get(key);
          const stillDeclined = declinedAt !== undefined && units <= declinedAt;
          if (!stillDeclined && !blocked) {
            adds.push({ id: Number(rule.gift.variant), quantity: wanted, properties: { _free_gift: key } });
            touchedKeys.add(key);
          }
        } else if (!matches && lines.length) {
          lines.forEach((line) => { updates[line.key] = 0; });
          touchedKeys.add(key);
        } else if (matches && lines.length) {
          // keep exactly one gift line at the configured quantity
          lines.slice(1).forEach((line) => { updates[line.key] = 0; });
          if (lines[0].quantity !== wanted) updates[lines[0].key] = wanted;
          if (lines.length > 1 || lines[0].quantity !== wanted) touchedKeys.add(key);
        }
        if (!matches && declined.has(key)) {
          declined.delete(key);
          this.declinedGifts = declined;
        }
      }

      // Gift lines no current rule owns (rule disabled or deleted, or a B2B
      // cart, which gets no rules) are removed rather than left in for free.
      const ruleKeys = new Set(rules.map((rule) => String(rule.key)));
      giftLines.forEach((line) => {
        const key = String(line.properties._free_gift);
        if (ruleKeys.has(key)) return;
        updates[line.key] = 0;
        touchedKeys.add(key);
      });

      return { adds, updates, touchedKeys };
    }

    /* ---------------------------------------------------- integrations */

    registerGlobals() {
      const drawer = this;
      window.upcartOpenCart = () => drawer.open();
      window.upcartCloseCart = () => drawer.close();
      window.upcartRefreshCart = () => drawer.refresh();
      window.upcartToggleCart = () => drawer.toggle();
      window.cartDrawer = drawer;

      if (window.__cartDrawerInterceptorsInstalled) return;
      window.__cartDrawerInterceptorsInstalled = true;

      const isInternal = (headers) => {
        if (!headers) return false;
        if (typeof headers.get === 'function') return !!headers.get(INTERNAL_HEADER);
        return !!headers[INTERNAL_HEADER];
      };
      const onCartRequest = (url) => {
        const active = window.cartDrawer;
        if (!active) return;
        if (/\/cart\/add/.test(url) && active.openOnAdd) {
          active.setBusy(true);
          active.open();
        }
        active.scheduleRefresh();
      };

      // fetch() — the theme (base.js), Seal Subscriptions and most apps use it
      const originalFetch = window.fetch;
      window.fetch = function (input, init) {
        const url = typeof input === 'string' ? input : input && input.url ? input.url : '';
        const promise = originalFetch.apply(this, arguments);
        if (CART_REQUEST.test(url) && !isInternal(init && init.headers) && !(input instanceof Request && isInternal(input.headers))) {
          promise
            .then((response) => {
              if (response.ok) onCartRequest(url);
            })
            .catch(() => {});
        }
        return promise;
      };

      // XMLHttpRequest — older app scripts / jQuery
      const originalOpen = XMLHttpRequest.prototype.open;
      const originalSend = XMLHttpRequest.prototype.send;
      XMLHttpRequest.prototype.open = function (method, url) {
        this.__cartDrawerUrl = typeof url === 'string' ? url : String(url);
        return originalOpen.apply(this, arguments);
      };
      XMLHttpRequest.prototype.send = function () {
        if (this.__cartDrawerUrl && CART_REQUEST.test(this.__cartDrawerUrl)) {
          this.addEventListener('load', () => {
            if (this.status >= 200 && this.status < 300) onCartRequest(this.__cartDrawerUrl);
          });
        }
        return originalSend.apply(this, arguments);
      };
    }
  }

  customElements.define('cart-drawer', CartDrawer);
})();
